<!--
  PersonalisationSidebar — contextual sidebar contents for the
  "Personalisation" cauldron.

  Lists every Mijn-omgeving sub-route as its own row, plus the standalone
  /alerts page (alerts are user-owned objects too; they live alongside the
  rest of "things I created" per the cauldron-segregation decisions).

  Sub-route labels reuse the existing i18n keys from the Mijn omgeving
  sections, plus existing labels for adjacent user-owned pages.
-->
<template>
  <div class="flex flex-col h-full">
    <nav class="flex-1 overflow-y-auto px-3 py-3" aria-label="Personalisation navigation">
      <div v-if="!collapsed" class="sidebar-section-label">
        {{ t('cauldron.personalisation.label') }}
      </div>

      <sidebar-menu-item
        v-for="entry in personalisationEntries"
        :key="entry.to"
        :to="entry.to"
        :label="t(entry.labelKey)"
        :icon="entry.icon"
        :collapsed="collapsed"
        :active-match="entry.activeMatch"
        @click="emit('navigate')"
      />
    </nav>
  </div>
</template>

<script setup>
  /* eslint-disable no-undef */
  import SidebarMenuItem from '@/components/navigation/SidebarMenuItem.vue';
  import {
    mdiBellOutline,
    mdiChartLine,
    mdiChartTimelineVariant,
    mdiCounter,
    mdiDownloadOutline,
    mdiFileChartOutline,
    mdiScriptTextPlayOutline,
    mdiViewDashboardOutline,
    mdiViewGridOutline,
  } from '@mdi/js';
  import { useFeatureFlags } from '@/composables/useFeatureFlags';
  import { useI18n } from 'vue-i18n';
  import { computed } from 'vue';

  defineProps({
    collapsed: { type: Boolean, default: false },
  });
  const emit = defineEmits(['navigate']);

  const { t } = useI18n();
  const { isFeatureEnabled } = useFeatureFlags();

  // The order mirrors the Mijn-omgeving page's own section tabs so the
  // sidebar and the in-page nav stay aligned.
  const personalisationEntries = computed(() => [
    {
      to: '/my-environment/overview',
      labelKey: 'mijn_omgeving.sections.overzicht',
      icon: mdiViewGridOutline,
      activeMatch: 'exact',
    },
    {
      to: '/my-environment/dashboards',
      labelKey: 'mijn_omgeving.sections.dashboards',
      icon: mdiViewDashboardOutline,
      activeMatch: 'prefix',
    },
    {
      to: '/my-environment/kpis',
      labelKey: 'mijn_omgeving.sections.kpis',
      icon: mdiCounter,
      activeMatch: 'prefix',
    },
    {
      to: '/my-environment/charts',
      labelKey: 'mijn_omgeving.sections.charts',
      icon: mdiChartLine,
      activeMatch: 'prefix',
    },
    {
      to: '/my-environment/profiles',
      labelKey: 'mijn_omgeving.sections.profiles',
      icon: mdiChartTimelineVariant,
      activeMatch: 'prefix',
    },
    {
      to: '/my-environment/toolruns',
      labelKey: 'mijn_omgeving.sections.toolruns',
      icon: mdiScriptTextPlayOutline,
      activeMatch: 'prefix',
    },
    // Reports — gated by the report-builder flag so we never show a dead link
    // whose route guard would just bounce the user home.
    ...(isFeatureEnabled('report-builder')
      ? [
          {
            to: '/my-environment/reports',
            labelKey: 'mijn_omgeving.sections.reports',
            icon: mdiFileChartOutline,
            activeMatch: 'prefix',
          },
        ]
      : []),
    {
      to: '/my-environment/exports',
      labelKey: 'export_jobs.menu_label',
      icon: mdiDownloadOutline,
      activeMatch: 'prefix',
    },
    // Alerts standalone — user-owned, but lives at /alerts (legacy route)
    // rather than under /my-environment. Surfaced here so all user-owned
    // objects are in one mental category.
    {
      to: '/alerts',
      labelKey: 'mijn_omgeving.sections.alerts',
      icon: mdiBellOutline,
      activeMatch: 'prefix',
    },
  ]);
</script>
